/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ResourceAdminState } from '@app/app/page-modules/resource/store/admin/resource-admin.state';
import { CurrentLanguage } from '@app/app/page-modules/resource/store/admin/resource-event-admin.state.model';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import { SnackbarHelper } from '@app/app/shared/helpers/snackbar-helper';
import { Form, LanguageValue, Organization, QuizFormContent } from '@app/app/shared/models';
import { Quiz, QuizSettings, UpdateQuizRequest, createQuiz } from '@app/app/shared/models/editor/quiz-content.model';
import { SupportedLanguage } from '@app/app/shared/models/languages/languages.model';
import { UserAuthState } from '@app/app/user-auth/store/user-auth.state';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { DATA_SERVICE, QuizDataService } from '../../../services/data.service';

@Component({
  selector: 'ptl-quiz',
  templateUrl: './quiz.component.html',
  styleUrls: ['../form/form.component.scss', './quiz.component.scss'],
})
export class QuizComponent implements OnInit, OnChanges, OnDestroy {
  @Input() quizContent: Form<QuizFormContent>;
  @Input() cardUid?: string;

  private _canRemoveItem: boolean | undefined;

  @Input()
  set form(value: Form<QuizFormContent> | undefined) {
    if (typeof value === 'string') {
      this.quizContent = JSON.parse(decodeURIComponent(value));
    } else {
      this.quizContent = value;
    }
  }

  get form() {
    return this.quizContent;
  }

  @Input()
  set canRemoveItem(value) {
    if (typeof value === 'string') {
      this._canRemoveItem = JSON.parse(decodeURIComponent(value));
    } else {
      this._canRemoveItem = value;
    }
  }

  get canRemoveItem() {
    return this._canRemoveItem;
  }

  @Select(ResourceAdminState.currentLanguage)
  private currentLanguage$: Observable<CurrentLanguage>;

  @Select(UserAuthState.organizationDetails)
  private organization$: Observable<Organization>;

  /** Emits removing of the quizContent event */
  @Output() removeQuizContent = new EventEmitter<void>();

  /** Emits the update of the quizContent event */
  @Output() updateQuizContent = new EventEmitter<Form<QuizFormContent>>();

  quiz: Quiz = createQuiz();
  languageCode: string;
  currentLanguage: SupportedLanguage;
  quizTitle: LanguageValue = { languageCode: 'en', value: '' };
  quizChanged = false;
  resourceUid: string;
  titleChanged = false;
  expanded = false;
  organization: Organization;
  focused = false;
  saveInProgress = false;
  forceExpand = false;
  effectiveCardUid: string;

  private subscriptionEnd$ = new EventEmitter<void>();
  private quizLoaded = false;

  constructor(
    @Inject(DATA_SERVICE) private dataService: QuizDataService,
    private cd: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    private ngZone: NgZone,
    private store: Store,
  ) {
    this.resourceUid = this.store.selectSnapshot(ResourceAdminState.resourceUid);
  }

  ngOnInit() {
    this.effectiveCardUid = this.cardUid || this.resourceUid;
    console.log('[Quiz] ngOnInit - cardUid:', this.cardUid, 'resourceUid:', this.resourceUid, 'effectiveCardUid:', this.effectiveCardUid);
    console.log('[Quiz] ngOnInit - quizContent:', this.quizContent);
    console.log('[Quiz] ngOnInit - quizId:', this.quizContent?.content?.quizId, 'newAddedForm:', this.quizContent?.newAddedForm);
    this.organization$.pipe(take(1)).subscribe((org) => (this.organization = org));
    this.currentLanguage$.pipe(takeUntil(this.subscriptionEnd$)).subscribe((language) => {
      if (language) {
        this.currentLanguage = language.supportedLanguage;
        this.languageCode = language.supportedLanguage?.language?.code;
        console.log('[Quiz] ngOnInit - language received:', this.languageCode);
        this.handleQuizInitialization();
      }
    });
  }

  private handleQuizInitialization() {
    const quizId = this.quizContent?.content?.quizId;
    const hasQuizId = quizId && typeof quizId === 'string' && quizId.trim() !== '';
    const isNewQuiz = !hasQuizId;
    console.log(
      '[Quiz] handleQuizInitialization - quizId:',
      quizId,
      'hasQuizId:',
      hasQuizId,
      'isNewQuiz:',
      isNewQuiz,
      'newAddedForm:',
      this.quizContent?.newAddedForm,
      'effectiveCardUid:',
      this.effectiveCardUid,
    );
    if (hasQuizId && !this.quizLoaded) {
      console.log('[Quiz] handleQuizInitialization - loading existing quiz');
      this.getQuizData(quizId);
      this.quizLoaded = true;
    } else if (isNewQuiz) {
      console.log('[Quiz] handleQuizInitialization - new quiz, checking cardUid');
      if (this.effectiveCardUid) {
        console.log('[Quiz] handleQuizInitialization - calling createQuizIfNeeded with cardUid:', this.effectiveCardUid);
        this.createQuizIfNeeded(this.effectiveCardUid);
      } else {
        console.log('[Quiz] handleQuizInitialization - no cardUid, skipping createQuiz');
        this.quizTitle = {
          languageCode: this.languageCode || 'en',
          value: this.quizContent?.content?.title || 'Quiz',
        };
        this.expanded = true;
        this.forceExpand = this.quizContent?.content?.forceExpand || false;
      }
    }
  }

  ngOnDestroy() {
    this.subscriptionEnd$.emit();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.quizContent && changes.quizContent.previousValue && changes.quizContent.currentValue) {
      const currentQuizUid = changes.quizContent?.currentValue?.content.quizId;
      const previousQuizUid = changes.quizContent?.previousValue?.content.quizId;
      const currentQuizId = currentQuizUid && typeof currentQuizUid === 'string' && currentQuizUid.trim() !== '' ? currentQuizUid : null;
      const previousQuizId =
        previousQuizUid && typeof previousQuizUid === 'string' && previousQuizUid.trim() !== '' ? previousQuizUid : null;

      // Handle language change
      if (this.quizLoaded && currentQuizId === previousQuizId && currentQuizId) {
        this.getQuizData(currentQuizId);
      }
      // Handle case when previous state undefined e.g. creating new quiz
      if (!previousQuizId && currentQuizId) {
        this.getQuizData(currentQuizId);
      }
      // Handle case when quiz content changes but no quizId yet
      if (!currentQuizId && this.effectiveCardUid && this.languageCode) {
        this.handleQuizInitialization();
      }
    }
    if (changes.cardUid) {
      console.log('[Quiz] ngOnChanges - cardUid changed:', changes.cardUid.previousValue, '->', changes.cardUid.currentValue);
      this.effectiveCardUid = this.cardUid || this.resourceUid;
      const quizId = this.quizContent?.content?.quizId;
      const hasQuizId = quizId && typeof quizId === 'string' && quizId.trim() !== '';
      console.log(
        '[Quiz] ngOnChanges - effectiveCardUid:',
        this.effectiveCardUid,
        'hasQuizId:',
        hasQuizId,
        'newAddedForm:',
        this.quizContent?.newAddedForm,
      );
      if (!hasQuizId && this.effectiveCardUid) {
        if (this.languageCode) {
          console.log('[Quiz] ngOnChanges - languageCode available, calling handleQuizInitialization');
          this.handleQuizInitialization();
        } else {
          console.log('[Quiz] ngOnChanges - waiting for languageCode');
          this.currentLanguage$.pipe(take(1), takeUntil(this.subscriptionEnd$)).subscribe((language) => {
            if (language) {
              this.currentLanguage = language.supportedLanguage;
              this.languageCode = language.supportedLanguage?.language?.code;
              this.handleQuizInitialization();
            }
          });
        }
      }
    }
  }

  saveQuiz() {
    if (this.quizChanged) {
      this.quizChanged = false;
      if (this.titleChanged) {
        this.titleChanged = false;
        this.quizContent = {
          type: 'FORM',
          newAddedForm: false,
          uid: this.quizContent.uid ? this.quizContent.uid : null,
          content: {
            title: this.quizTitle ? this.quizTitle['value'] : null,
            quizId: this.quiz._id,
            type: 'QUIZ',
            submitted: false,
            formIsActive: false,
          },
        };

        this.updateQuizContent.emit(this.quizContent);
      }
      const request = {
        title: this.quizTitle,
        instruction: this.quiz.instruction,
        settings: this.quiz.settings,
      } as UpdateQuizRequest;
      this.saveInProgress = true;
      this.dataService
        .updateQuiz(this.effectiveCardUid, this.quiz._id, request, this.languageCode)
        .subscribe(({ isSuccess, value, error }) => {
          this.saveInProgress = false;
          if (isSuccess) {
            this.quiz = value;
            this.quizTitle = LanguageCodeHelper.getDataByUserLanguageCode(
              this.quiz.title as LanguageValue[],
              this.organization,
              this.languageCode,
            );
            this.expanded = false;
            this.forceExpand = false;
          } else {
            SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error, 4000);
            this.quizChanged = true;
          }
        });
    }

    this.expanded = false;
    this.forceExpand = false;
  }

  onQuizElementRemoved() {
    if (this.quiz._id) {
      this.dataService.deleteQuiz(this.effectiveCardUid, this.quiz._id).subscribe();
    }
    this.removeQuizContent.emit();
  }

  onQuizTitleUpdate(value: string) {
    this.titleChanged = true;
    this.quizChanged = true;
    Object.assign(this.quiz.title, {
      languageCode: this.languageCode,
      value,
    });
    this.quizTitle = {
      languageCode: this.languageCode || 'en',
      value: value,
    };
  }

  onInstructionValueChanged(value: LanguageValue) {
    this.quizChanged = true;
    this.quiz.instruction = value;
  }

  onSettingsValueChanged(value: QuizSettings) {
    this.quizChanged = true;
    this.quiz.settings = value;
  }

  private getQuizData(quizUid: string) {
    this.dataService.getQuiz(this.effectiveCardUid, quizUid, this.languageCode).subscribe(({ isSuccess, value }) => {
      this.quizLoaded = true;
      if (isSuccess) {
        this.quiz = value;
        this.quizTitle = LanguageCodeHelper.getDataByUserLanguageCode(
          this.quiz.title as LanguageValue[],
          this.organization,
          this.languageCode,
        );

        if (this.quiz.instructions.length === 0) {
          this.expanded = true;
        }
      } else {
        this.quizTitle = {
          languageCode: '',
          value: '',
        };
      }
    });
  }

  private createQuizIfNeeded(cardUid: string) {
    const quizId = this.quizContent?.content?.quizId;
    const hasQuizId = quizId && typeof quizId === 'string' && quizId.trim() !== '';
    console.log('[Quiz] createQuizIfNeeded - called with cardUid:', cardUid, 'hasQuizId:', hasQuizId, 'quizId:', quizId);
    if (!hasQuizId && cardUid) {
      console.log('[Quiz] createQuizIfNeeded - calling createQuiz API with cardUid:', cardUid, 'languageCode:', this.languageCode);
      this.dataService.createQuiz(cardUid, this.languageCode).subscribe(({ isSuccess, value, error }) => {
        console.log('[Quiz] createQuizIfNeeded - API response:', { isSuccess, value, error });
        if (isSuccess && value?._id) {
          this.quiz = value;
          this.quizContent = {
            ...this.quizContent,
            content: {
              ...this.quizContent.content,
              quizId: value._id,
            },
          };
          this.updateQuizContent.emit(this.quizContent);
          this.quizTitle = LanguageCodeHelper.getDataByUserLanguageCode(
            this.quiz.title as LanguageValue[],
            this.organization,
            this.languageCode,
          );
          this.expanded = true;
          this.forceExpand = this.quizContent?.content?.forceExpand || false;
        }
      });
    }
  }
}
